/**
 * HR Review Page
 *
 * Detailed view of an evaluation for HR review and approval.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import {
  useEvaluation,
  useCalculateScores,
  useUpdateEvaluationStatus,
} from '../evaluations/useEvaluations';
import { useEmployeeAssignedTasks } from '../employees/useEmployeeTasks';
import { ScoreDisplay, SCORE_LABELS } from '../evaluations/wizard/ScoreInput';
import { useScoreCriteria } from '../settings/useSettings';
import { ApiRequestError } from '../../lib/api';
import type { EvaluationStatus } from '../../lib/evaluationsApi';
import type { ScoreCriteriaDTO } from '@functieloonhuis/shared';

const STATUS_CONFIG: Record<EvaluationStatus, { label: string; color: string; nextStatus?: EvaluationStatus; nextLabel?: string }> = {
  draft: { label: 'Concept', color: 'bg-yellow-100 text-yellow-800' },
  submitted: { label: 'Ingediend', color: 'bg-blue-100 text-blue-800', nextStatus: 'in_review', nextLabel: 'Start Review' },
  in_review: { label: 'In Review', color: 'bg-purple-100 text-purple-800', nextStatus: 'final', nextLabel: 'Accorderen' },
  final: { label: 'Definitief', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Geannuleerd', color: 'bg-gray-100 text-gray-600' },
};

// Helper component for displaying score criteria with baseline comparison
function ScoreCriteriaDisplay({
  score,
  contextType,
  contextId
}: {
  score: number;
  contextType: 'task' | 'competency' | 'general';
  contextId?: number;
}) {
  // Fetch task/competency-specific criteria, fall back to general
  const { data: specificCriteria } = useScoreCriteria(contextType, contextId);
  const { data: generalCriteria } = useScoreCriteria('general');

  // Helper to get criteria for a specific score level
  const getCriteria = (scoreLevel: number) => {
    return specificCriteria?.find((c: ScoreCriteriaDTO) => c.scoreLevel === scoreLevel)
      || generalCriteria?.find((c: ScoreCriteriaDTO) => c.scoreLevel === scoreLevel);
  };

  // Get criteria for actual score and baseline (score 3 = Voldoende)
  const actualCriteria = getCriteria(score);
  const baselineCriteria = score !== 3 ? getCriteria(3) : null;

  if (!actualCriteria && !baselineCriteria) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Baseline "Voldoende" criteria - shown when score differs from 3 */}
      {baselineCriteria && (
        <div className="bg-yellow-50 rounded p-3 border border-yellow-200">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-xs font-semibold text-yellow-800 mb-1">
                Referentie: Score 3 - {baselineCriteria.label} (verwacht niveau)
              </p>
              <p className="text-xs text-yellow-700">{baselineCriteria.description}</p>
              {baselineCriteria.examples && (
                <p className="text-xs text-yellow-600 italic mt-1">
                  Voorbeelden: {baselineCriteria.examples}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actual score criteria */}
      {actualCriteria && (
        <div className={`rounded p-3 border ${
          score > 3
            ? 'bg-green-50 border-green-200'
            : score < 3
              ? 'bg-red-50 border-red-200'
              : 'bg-gray-100 border-gray-200'
        }`}>
          <div className="flex items-start gap-2">
            <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              score > 3 ? 'text-green-600' : score < 3 ? 'text-red-600' : 'text-gray-500'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className={`text-xs font-semibold mb-1 ${
                score > 3 ? 'text-green-800' : score < 3 ? 'text-red-800' : 'text-gray-700'
              }`}>
                {score !== 3 ? 'Gegeven: ' : ''}Score {score} - {actualCriteria.label}
              </p>
              <p className={`text-xs ${
                score > 3 ? 'text-green-700' : score < 3 ? 'text-red-700' : 'text-gray-600'
              }`}>{actualCriteria.description}</p>
              {actualCriteria.examples && (
                <p className={`text-xs italic mt-1 ${
                  score > 3 ? 'text-green-600' : score < 3 ? 'text-red-600' : 'text-gray-500'
                }`}>
                  Voorbeelden: {actualCriteria.examples}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function HRReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const evaluationId = parseInt(id || '', 10);

  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);
  const { data: assignedTasks } = useEmployeeAssignedTasks(evaluation?.employeeId || 0);
  const { data: scores } = useCalculateScores(evaluationId);
  const updateStatus = useUpdateEvaluationStatus();

  const [hrNotes, setHrNotes] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());
  const [expandedCompetencies, setExpandedCompetencies] = useState<Set<number>>(new Set());
  const [isPrintMode, setIsPrintMode] = useState(false);
  // Epic 4 (Story 4.4, AC #2, AD-5): finalize van een core_tasks_v2-
  // beoordeling kan weigeren met PACKAGE_CHANGED (409) als het pakket sinds
  // het scoren is gewijzigd — welke tijdvakken opnieuw gescoord moeten
  // worden staat in `.details.affectedPeriodIds`.
  const [packageChangedPeriodIds, setPackageChangedPeriodIds] = useState<number[] | null>(null);

  const toggleTaskExpanded = (taskId: number) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleCompetencyExpanded = (competencyId: number) => {
    setExpandedCompetencies(prev => {
      const next = new Set(prev);
      if (next.has(competencyId)) {
        next.delete(competencyId);
      } else {
        next.add(competencyId);
      }
      return next;
    });
  };

  // Handle print - expand all tasks and competencies and trigger print
  const handlePrint = () => {
    // Expand all tasks for printing
    const allTaskIds = new Set(assignedTasks?.tasks?.map(t => t.id) || []);
    setExpandedTasks(allTaskIds);

    // Expand all competencies for printing
    const allVakCompIds = new Set(scores?.breakdown?.vakCompetencies?.map(c => c.id) || []);
    const allBasisCompIds = new Set(scores?.breakdown?.basisCompetencies?.map(c => c.id) || []);
    setExpandedCompetencies(new Set([...allVakCompIds, ...allBasisCompIds]));
    setIsPrintMode(true);

    // Wait for state update and render, then print
    setTimeout(() => {
      window.print();
      // Reset after print dialog closes
      setTimeout(() => {
        setIsPrintMode(false);
      }, 500);
    }, 100);
  };

  if (isLoadingEval) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Laden...</span>
        </div>
      </Layout>
    );
  }

  if (!evaluation) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">Beoordeling niet gevonden.</p>
            <button
              onClick={() => navigate('/hr')}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              Terug naar overzicht
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const statusConfig = STATUS_CONFIG[evaluation.status];
  const selfEvaluations = evaluation.selfEvaluations || [];
  const taskScores = evaluation.taskScores || [];
  const competencyScores = evaluation.competencyScores || [];

  // Dynamic colors based on score level for total score card
  const getScoreColors = (level: number | undefined) => {
    switch (level) {
      case 1: // Onvoldoende
        return {
          bg: 'bg-gradient-to-br from-red-50 to-red-100',
          border: 'border-red-200',
          icon: 'bg-red-200',
          iconText: 'text-red-600',
          text: 'text-red-700',
          score: 'text-red-900',
          label: 'text-red-600',
        };
      case 2: // Matig
        return {
          bg: 'bg-gradient-to-br from-orange-50 to-orange-100',
          border: 'border-orange-200',
          icon: 'bg-orange-200',
          iconText: 'text-orange-600',
          text: 'text-orange-700',
          score: 'text-orange-900',
          label: 'text-orange-600',
        };
      case 3: // Voldoende
        return {
          bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
          border: 'border-yellow-200',
          icon: 'bg-yellow-200',
          iconText: 'text-yellow-600',
          text: 'text-yellow-700',
          score: 'text-yellow-900',
          label: 'text-yellow-600',
        };
      case 4: // Goed
        return {
          bg: 'bg-gradient-to-br from-lime-50 to-lime-100',
          border: 'border-lime-200',
          icon: 'bg-lime-200',
          iconText: 'text-lime-600',
          text: 'text-lime-700',
          score: 'text-lime-900',
          label: 'text-lime-600',
        };
      case 5: // Uitzonderlijk
        return {
          bg: 'bg-gradient-to-br from-green-50 to-green-100',
          border: 'border-green-200',
          icon: 'bg-green-200',
          iconText: 'text-green-600',
          text: 'text-green-700',
          score: 'text-green-900',
          label: 'text-green-600',
        };
      default: // Fallback to green
        return {
          bg: 'bg-gradient-to-br from-green-50 to-green-100',
          border: 'border-green-200',
          icon: 'bg-green-200',
          iconText: 'text-green-600',
          text: 'text-green-700',
          score: 'text-green-900',
          label: 'text-green-600',
        };
    }
  };

  const scoreColors = getScoreColors(scores?.totalScoreLevel);

  const handleStatusChange = async () => {
    if (!statusConfig.nextStatus) return;
    setPackageChangedPeriodIds(null);
    try {
      await updateStatus.mutateAsync({ id: evaluationId, status: statusConfig.nextStatus });
      setShowConfirm(false);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'PACKAGE_CHANGED') {
        const details = err.details as { affectedPeriodIds?: number[] } | undefined;
        setPackageChangedPeriodIds(details?.affectedPeriodIds ?? []);
        setShowConfirm(false);
        return;
      }
      throw err;
    }
  };

  return (
    <Layout>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          /* Hide navigation and layout elements */
          nav, header, footer, .sidebar, [role="navigation"] {
            display: none !important;
          }

          /* Reset body and container for print */
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          /* Ensure full width for print */
          .max-w-5xl {
            max-width: 100% !important;
          }

          /* Ensure backgrounds print */
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Page breaks */
          .page-break-before {
            page-break-before: always;
          }

          /* Avoid breaking inside task cards */
          .task-card {
            page-break-inside: avoid;
          }

          /* Print header styling */
          h1 {
            font-size: 18pt !important;
          }

          /* Reduce padding for print */
          .p-4, .px-4, .py-4 {
            padding: 8px !important;
          }

          /* Ensure score criteria boxes print nicely */
          .bg-yellow-50, .bg-green-50, .bg-red-50, .bg-gray-100 {
            border: 1px solid #ccc !important;
          }
        }
      `}</style>
      <div data-testid="hr-review-page" className="max-w-5xl mx-auto print:max-w-none">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <button
              onClick={() => navigate('/hr')}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2 print:hidden"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Terug naar overzicht
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              HR Review: {evaluation.employeeName || `Medewerker #${evaluation.employeeId}`}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Periode: {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL')} -{' '}
              {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL')}
            </p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              title="Afdrukken"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Afdrukken
            </button>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
            {statusConfig.nextStatus && (
              <button
                onClick={() => setShowConfirm(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {statusConfig.nextLabel}
              </button>
            )}
          </div>
        </div>

        {/* Score Summary Cards */}
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Task Score */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-center">
            <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <p className="text-xs text-gray-500 mb-1">Taken ({scores?.weights?.task ?? 50}pt)</p>
            <p className="text-2xl font-bold text-gray-900">
              {scores?.taskScore?.toFixed(1) ?? '—'}
            </p>
            <p className="text-xs text-gray-400">/ {scores?.weights?.task ?? 50}</p>
          </div>

          {/* Vak Competency Score */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-center">
            <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-orange-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <p className="text-xs text-gray-500 mb-1">Vakcomp. ({scores?.weights?.vakCompetency ?? 30}pt)</p>
            <p className="text-2xl font-bold text-gray-900">
              {scores?.vakCompetencyScore?.toFixed(1) ?? '—'}
            </p>
            <p className="text-xs text-gray-400">/ {scores?.weights?.vakCompetency ?? 30}</p>
          </div>

          {/* Basis Competency Score */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-center">
            <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p className="text-xs text-gray-500 mb-1">Basiscomp. ({scores?.weights?.basisCompetency ?? 20}pt)</p>
            <p className="text-2xl font-bold text-gray-900">
              {scores?.basisCompetencyScore?.toFixed(1) ?? '—'}
            </p>
            <p className="text-xs text-gray-400">/ {scores?.weights?.basisCompetency ?? 20}</p>
          </div>

          {/* Total Score */}
          <div className={`${scoreColors.bg} rounded-lg border ${scoreColors.border} shadow-sm p-4 text-center`}>
            <div className={`w-10 h-10 mx-auto mb-2 rounded-full ${scoreColors.icon} flex items-center justify-center`}>
              <svg className={`w-5 h-5 ${scoreColors.iconText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <p className={`text-xs ${scoreColors.text} mb-1`}>Totaal</p>
            <p className={`text-3xl font-bold ${scoreColors.score}`}>
              {scores?.totalScore?.toFixed(1) ?? '—'}
            </p>
            <p className={`text-xs ${scoreColors.text}`}>/ 100</p>
            {scores?.totalScoreLabel && (
              <p className={`text-sm font-semibold ${scoreColors.label} mt-2 pt-2 border-t ${scoreColors.border}`}>
                {scores.totalScoreLabel}
              </p>
            )}
          </div>
        </div>

        {/* Detailed Task Scores */}
        <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Taakscores Detail</h3>
            <p className="text-sm text-gray-500 mt-1">Klik op een taak voor meer details</p>
          </div>
          <div className="divide-y divide-gray-200">
            {assignedTasks?.tasks?.map((task) => {
              const selfEval = selfEvaluations.find((se) => se.taskId === task.id);
              const managerScore = taskScores.find((ts) => ts.taskId === task.id);
              const diff = selfEval && managerScore ? managerScore.score - selfEval.score : null;
              const isExpanded = expandedTasks.has(task.id) || isPrintMode;
              const hasDiff = diff !== null && Math.abs(diff) >= 2;
              const hasDetails = selfEval?.toelichting || managerScore?.toelichting;

              return (
                <div key={task.id} className={hasDiff ? 'bg-red-50' : ''}>
                  {/* Main row - clickable */}
                  <button
                    onClick={() => toggleTaskExpanded(task.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleTaskExpanded(task.id);
                      }
                    }}
                    aria-expanded={isExpanded}
                    aria-label={`${task.name} - ${isExpanded ? 'Inklappen' : 'Uitklappen'} voor details`}
                    className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                  >
                    {/* Expand icon */}
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>

                    {/* Task name and weight */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{task.name}</p>
                      <p className="text-xs text-gray-500">Weging: {task.weging}%</p>
                    </div>

                    {/* Self evaluation score */}
                    <div className="text-center w-28">
                      <p className="text-xs text-yellow-600 font-medium mb-1">Zelf</p>
                      {selfEval ? (
                        <div>
                          <ScoreDisplay score={selfEval.score} size="sm" showLabel={false} />
                          <p className="text-xs text-gray-500 mt-0.5">{SCORE_LABELS[selfEval.score]}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </div>

                    {/* Manager score */}
                    <div className="text-center w-28">
                      <p className="text-xs text-blue-600 font-medium mb-1">Manager</p>
                      {managerScore ? (
                        <div>
                          <ScoreDisplay score={managerScore.score} size="sm" showLabel={false} />
                          <p className="text-xs text-gray-500 mt-0.5">{SCORE_LABELS[managerScore.score]}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </div>

                    {/* Difference */}
                    <div className="text-center w-20">
                      <p className="text-xs text-gray-500 font-medium mb-1">Verschil</p>
                      {diff !== null ? (
                        <span className={`text-sm font-bold ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {diff > 0 ? '+' : ''}{diff}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </div>

                    {/* Has details indicator */}
                    {hasDetails && (
                      <div className="w-6">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                      </div>
                    )}
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 ml-9 space-y-3">
                      {/* Task description */}
                      {task.beschrijving && (
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <p className="text-xs font-semibold text-gray-700 mb-1">Taakbeschrijving</p>
                              <p className="text-sm text-gray-600">{task.beschrijving}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Self evaluation details */}
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-full bg-yellow-200 flex items-center justify-center">
                              <svg className="w-4 h-4 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <h4 className="text-sm font-semibold text-yellow-800">Zelf-evaluatie</h4>
                          </div>
                          {selfEval ? (
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <ScoreDisplay score={selfEval.score} size="sm" showLabel={true} />
                              </div>
                              {selfEval.toelichting ? (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-yellow-700 mb-1">Toelichting:</p>
                                  <p className="text-sm text-gray-700 bg-white rounded p-2 border border-yellow-100">
                                    {selfEval.toelichting}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-xs text-yellow-600 italic">Geen toelichting gegeven</p>
                              )}
                            </>
                          ) : (
                            <p className="text-sm text-yellow-600 italic">Geen zelf-evaluatie ingevuld</p>
                          )}
                        </div>

                        {/* Manager evaluation details */}
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center">
                              <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            </div>
                            <h4 className="text-sm font-semibold text-blue-800">Manager beoordeling</h4>
                          </div>
                          {managerScore ? (
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <ScoreDisplay score={managerScore.score} size="sm" showLabel={true} />
                              </div>
                              {managerScore.toelichting ? (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-blue-700 mb-1">Toelichting:</p>
                                  <p className="text-sm text-gray-700 bg-white rounded p-2 border border-blue-100">
                                    {managerScore.toelichting}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-xs text-blue-600 italic">Geen toelichting gegeven</p>
                              )}
                              {/* Score criteria for manager's score */}
                              <ScoreCriteriaDisplay
                                score={managerScore.score}
                                contextType="task"
                                contextId={task.id}
                              />
                            </>
                          ) : (
                            <p className="text-sm text-blue-600 italic">Nog niet beoordeeld door manager</p>
                          )}
                        </div>
                      </div>

                      {/* Show significant difference warning */}
                      {hasDiff && (
                        <div className="bg-red-100 rounded-lg p-3 border border-red-200 flex items-start gap-2">
                          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-red-800">Significant verschil gedetecteerd</p>
                            <p className="text-xs text-red-600">
                              Er is een verschil van {diff !== null ? Math.abs(diff) : 0} punt(en) tussen de zelf-evaluatie en manager beoordeling.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Vakcompetenties */}
        {scores?.breakdown?.vakCompetencies && scores.breakdown.vakCompetencies.length > 0 && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-orange-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Vakcompetenties</h3>
                  <p className="text-sm text-gray-500">Gekoppeld aan specifieke taken</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-600">{scores.vakCompetencyScore?.toFixed(1)}</p>
                  <p className="text-xs text-gray-500">/ {scores.weights?.vakCompetency ?? 30} punten</p>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {scores.breakdown.vakCompetencies.map((comp) => {
                const rawScore = competencyScores.find((cs) => cs.competencyId === comp.id);
                const isExpanded = expandedCompetencies.has(comp.id) || isPrintMode;
                const hasDetails = rawScore?.toelichting;

                return (
                  <div key={comp.id}>
                    {/* Main row - clickable */}
                    <button
                      onClick={() => toggleCompetencyExpanded(comp.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleCompetencyExpanded(comp.id);
                        }
                      }}
                      aria-expanded={isExpanded}
                      aria-label={`${comp.name} - ${isExpanded ? 'Inklappen' : 'Uitklappen'} voor details`}
                      className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-inset"
                    >
                      {/* Expand icon */}
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>

                      {/* Competency name and linked task */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{comp.name}</p>
                        {comp.taskName && (
                          <p className="text-xs text-orange-600 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            Gekoppeld aan: {comp.taskName}
                          </p>
                        )}
                      </div>

                      {/* Score */}
                      <div className="text-right w-28">
                        <ScoreDisplay score={comp.score} size="sm" showLabel={false} />
                        <p className="text-xs text-gray-500 mt-0.5">{SCORE_LABELS[comp.score]}</p>
                        <p className="text-xs text-orange-600 font-medium">{comp.earnedPoints?.toFixed(1) ?? '—'} pt</p>
                      </div>

                      {/* Has details indicator */}
                      {hasDetails && (
                        <div className="w-6">
                          <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                        </div>
                      )}
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 ml-9 space-y-3">
                        {rawScore?.toelichting && (
                          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-full bg-orange-200 flex items-center justify-center">
                                <svg className="w-4 h-4 text-orange-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                              </div>
                              <h4 className="text-sm font-semibold text-orange-800">Toelichting manager</h4>
                            </div>
                            <p className="text-sm text-gray-700 bg-white rounded p-2 border border-orange-100">
                              {rawScore.toelichting}
                            </p>
                          </div>
                        )}
                        {/* Score criteria for competency */}
                        <ScoreCriteriaDisplay
                          score={comp.score}
                          contextType="competency"
                          contextId={comp.id}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Basiscompetenties */}
        {scores?.breakdown?.basisCompetencies && scores.breakdown.basisCompetencies.length > 0 && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-purple-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Basiscompetenties</h3>
                  <p className="text-sm text-gray-500">Algemene competenties voor alle medewerkers</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-600">{scores.basisCompetencyScore?.toFixed(1)}</p>
                  <p className="text-xs text-gray-500">/ {scores.weights?.basisCompetency ?? 20} punten</p>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {scores.breakdown.basisCompetencies.map((comp) => {
                const rawScore = competencyScores.find((cs) => cs.competencyId === comp.id);
                const isExpanded = expandedCompetencies.has(comp.id) || isPrintMode;
                const hasDetails = rawScore?.toelichting;

                return (
                  <div key={comp.id}>
                    {/* Main row - clickable */}
                    <button
                      onClick={() => toggleCompetencyExpanded(comp.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleCompetencyExpanded(comp.id);
                        }
                      }}
                      aria-expanded={isExpanded}
                      aria-label={`${comp.name} - ${isExpanded ? 'Inklappen' : 'Uitklappen'} voor details`}
                      className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
                    >
                      {/* Expand icon */}
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>

                      {/* Competency name */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{comp.name}</p>
                      </div>

                      {/* Score */}
                      <div className="text-right w-28">
                        <ScoreDisplay score={comp.score} size="sm" showLabel={false} />
                        <p className="text-xs text-gray-500 mt-0.5">{SCORE_LABELS[comp.score]}</p>
                        <p className="text-xs text-purple-600 font-medium">{comp.earnedPoints?.toFixed(1) ?? '—'} pt</p>
                      </div>

                      {/* Has details indicator */}
                      {hasDetails && (
                        <div className="w-6">
                          <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                        </div>
                      )}
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 ml-9 space-y-3">
                        {rawScore?.toelichting && (
                          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center">
                                <svg className="w-4 h-4 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                              </div>
                              <h4 className="text-sm font-semibold text-purple-800">Toelichting manager</h4>
                            </div>
                            <p className="text-sm text-gray-700 bg-white rounded p-2 border border-purple-100">
                              {rawScore.toelichting}
                            </p>
                          </div>
                        )}
                        {/* Score criteria for competency */}
                        <ScoreCriteriaDisplay
                          score={comp.score}
                          contextType="competency"
                          contextId={comp.id}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Manager Opmerkingen (Step 6) */}
        {evaluation.opmerkingen && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-indigo-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900">Opmerkingen Manager</h3>
              </div>
              <p className="text-sm text-gray-500 mt-1">Algemene opmerkingen of conclusies over de beoordeling</p>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {evaluation.opmerkingen}
              </p>
            </div>
          </div>
        )}

        {/* Agreements */}
        {evaluation.agreements && evaluation.agreements.length > 0 && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Afspraken ({evaluation.agreements.length})</h3>
            </div>
            <div className="p-4 space-y-3">
              {evaluation.agreements.map((agreement) => (
                <div key={agreement.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
                    {agreement.type}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{agreement.beschrijving}</p>
                    {agreement.deadline && (
                      <p className="text-xs text-gray-500 mt-1">
                        Deadline: {new Date(agreement.deadline).toLocaleDateString('nl-NL')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Print footer with date */}
        <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-xs text-gray-500">
          <p>Afgedrukt op: {new Date().toLocaleString('nl-NL', { dateStyle: 'full', timeStyle: 'short' })}</p>
          <p>Beoordeling ID: {evaluationId}</p>
        </div>

        {/* HR Notes - hidden in print */}
        <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6 print:hidden">
          <h3 className="text-lg font-medium text-gray-900 mb-4">HR Notities</h3>
          <textarea
            rows={4}
            value={hrNotes}
            onChange={(e) => setHrNotes(e.target.value)}
            placeholder="Interne notities voor HR..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-2 text-xs text-gray-500">
            Deze notities zijn alleen zichtbaar voor HR en worden niet gedeeld met medewerker of manager.
          </p>
        </div>

        {/* Epic 4 (Story 4.4, AC #2) — PACKAGE_CHANGED-melding: het pakket is
            gewijzigd sinds er gescoord is, finalize is geweigerd. Neutraal
            waarschuwingslabel (geen rood — dit is geen systeemfout, wel
            actie vereist), print:hidden zoals de rest van de HR-acties. */}
        {packageChangedPeriodIds && (
          <div
            data-testid="package-changed-banner"
            className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 print:hidden"
          >
            <h3 className="text-sm font-semibold text-orange-800 mb-1">Pakket gewijzigd sinds het scoren</h3>
            <p className="text-sm text-orange-700">
              {packageChangedPeriodIds.length > 0
                ? `Het kerntakenpakket is gewijzigd sinds er gescoord is. Tijdvak(ken) ${packageChangedPeriodIds.join(', ')} moeten opnieuw gescoord worden voordat deze beoordeling definitief gemaakt kan worden.`
                : 'Het kerntakenpakket is gewijzigd sinds er gescoord is. Scoor de betreffende tijdvakken opnieuw voordat deze beoordeling definitief gemaakt kan worden.'}
            </p>
          </div>
        )}

        {/* Confirmation Modal - hidden in print */}
        {showConfirm && statusConfig.nextStatus && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print:hidden">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {statusConfig.nextStatus === 'final' ? 'Beoordeling Accorderen?' : 'Start Review?'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {statusConfig.nextStatus === 'final'
                  ? 'Na accorderen wordt de beoordeling definitief en kan deze niet meer worden aangepast.'
                  : 'De beoordeling wordt gemarkeerd als "In Review".'}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleStatusChange}
                  disabled={updateStatus.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {updateStatus.isPending ? 'Bezig...' : 'Bevestigen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
